import cloneDeep from 'lodash/cloneDeep';
import mapValues from 'lodash/mapValues';

import { DesignData } from 'editor/src/store/design/types';
import getLayoutSchemaByName from 'editor/src/store/editor/selector/getLayoutSchemaByName';
import { RootState } from 'editor/src/store/index';
import getResizableProductForGivenVariant from 'editor/src/store/variants/selector/getResizableProductForGivenVariant';
import {
  Product,
  VariationGroup,
  VariationProductControl,
  ExistingVariant,
  Variant,
  ProductVariation,
  VariantInfo,
  AdditionalInfo,
} from 'editor/src/store/variants/types';

import applyResizableProductToDesign from 'editor/src/util/design/applyResizableProductToDesign';
import applyLayoutPerProductUid from 'editor/src/util/layouts/applyLayoutPerProductUid';
import reflectDesignData from 'editor/src/util/reflectDesignData';
import getReflectContext from 'editor/src/util/reflectDesignData/getReflectContext';

import getDesignKey from './getDesignKey';
import getDesignOptionKey from './getDesignOptionKey';
import getProductVariationSpecificTitle from './getProductVariationSpecificTitle';
import {
  getDesignsBySize,
  getSizeSpecificAreas,
  getVariationSize,
  withSizeSpecificAreasFrom,
} from './sizeSpecificAreas';

import type { i18n } from 'i18next';

// A group key doubles as the selected-group identity, and several call sites treat
// a falsy key as "nothing selected" (`getSelectedGroupKey`, `setProductDataReducer`).
// A product can legitimately carry NONE of the grouping axes the host asked to group
// by — single-colour apparel, or a product whose only axis is size — and those
// variants must still collapse into ONE selectable group rather than producing an
// empty key that no reducer can select. Not a valid attribute value, so it cannot
// collide with a real option.
export const SHARED_GROUP_KEY = '__all__';

export function makeGroupKey(variant: VariantInfo, groupingKeys: string[]): string {
  if (!groupingKeys.length) {
    return getDesignKey(variant.variation.productUid, variant);
  }

  const groupKey = groupingKeys.reduce((accumulatedKey, key) => {
    switch (key) {
      case 'product-size':
        return accumulatedKey + (variant.dimensions ? `${variant.dimensions.width}x${variant.dimensions.height}` : '');
      default:
        return accumulatedKey + (variant.variation[key] || '');
    }
  }, '');

  return groupKey || SHARED_GROUP_KEY;
}

function compareVariations(
  v1: ProductVariation,
  v2: ProductVariation,
  productControls: VariationProductControl[],
  controlValuesIndexMap: Map<string, Map<string, number>>,
) {
  for (let i = 0; i < productControls.length; i += 1) {
    const { key } = productControls[i];
    const valuesIndex = controlValuesIndexMap.get(key);
    const indexValue1 = valuesIndex?.get(v1[key]) ?? -1;
    const indexValue2 = valuesIndex?.get(v2[key]) ?? -1;
    if (indexValue1 !== indexValue2) {
      return indexValue1 - indexValue2;
    }
  }

  return 0;
}

export function sortGroups(groups: VariationGroup[], productControls: VariationProductControl[]): VariationGroup[] {
  const controlValuesIndexMap = new Map<string, Map<string, number>>();
  productControls.forEach((control) => {
    const indexes = new Map<string, number>();
    controlValuesIndexMap.set(control.key, indexes);
    control.options.forEach((option, i) => indexes.set(option.value, i));
  });

  groups.forEach((group) =>
    group.variationsInfo.sort((v1, v2) =>
      compareVariations(v1.variation, v2.variation, productControls, controlValuesIndexMap),
    ),
  );

  return groups.sort((group1, group2) =>
    compareVariations(
      group1.variationsInfo[0].variation,
      group2.variationsInfo[0].variation,
      productControls,
      controlValuesIndexMap,
    ),
  );
}

export function getGroupedVariations(variants: VariantInfo[], groupingKeys: string[]) {
  return variants.reduce<{ [groupKey: string]: VariantInfo[] }>((group, variant) => {
    const key = makeGroupKey(variant, groupingKeys);
    if (!group[key]) {
      group[key] = [];
    }
    group[key].push(variant);
    return group;
  }, {});
}

const EMPTY_ADDTIONAL_INFO: AdditionalInfo = {
  dimensions: undefined,
  pageCount: undefined,
};

export function areAdditionalInfoEquals(v1: AdditionalInfo, v2: AdditionalInfo) {
  return (
    v1.dimensions?.width === v2.dimensions?.width &&
    v1.dimensions?.height === v2.dimensions?.height &&
    v1.pageCount === v2.pageCount
  );
}

function isSameVariantOfAnyPageCount(v1: VariantInfo, v2: VariantInfo) {
  return (
    v1.variation.productUid === v2.variation.productUid &&
    areAdditionalInfoEquals(
      { dimensions: v1.dimensions, pageCount: undefined },
      { dimensions: v2.dimensions, pageCount: undefined },
    )
  );
}

type SourceVariantInfo = {
  designData: DesignData | undefined;
  linked: boolean;
  additionalInfo: AdditionalInfo;
  /** Each variant's own design, which holds its own size's artwork in the size-specific print areas. */
  ownDesigns: Map<VariantInfo, DesignData>;
};

export function getVariantInfoFromVariant(
  variants: VariantInfo[],
  existingVariants: ExistingVariant[],
): SourceVariantInfo {
  let existingVariant: ExistingVariant | undefined;
  let existingDesign: DesignData | undefined;

  // GPS-25623 — helper: a design "has media" if any spread carries at least
  // one media element. The customer's published variants come in two flavours
  // for the same group: the variant that was active during publish retains all
  // catalogue print-area spreads but receives NO media on the "Apply changes
  // to all variants" propagation step, while the other variants get the
  // slimmer single-spread payload with the actual artwork. Without this
  // preference the strip's per-Colour click-to-switch lands on the
  // empty-spread payload first and renders the canvas as an empty placeholder
  // even though a media-bearing sibling design is right next to it.
  const designHasMedia = (designData: DesignData | undefined): boolean => {
    if (!designData?.spreads?.length) {
      return false;
    }
    return designData.spreads.some((spread) => (spread?.pages?.[0]?.groups?.media?.length ?? 0) > 0);
  };

  // eslint-disable-next-line no-restricted-syntax
  for (const variant of variants) {
    const foundVariant = existingVariants.find((v) => {
      const dimensions = v.designData?.related_dimensions || v.designData?.dimensions;
      return (
        v.productUid === variant.variation.productUid &&
        areAdditionalInfoEquals({ dimensions, pageCount: undefined }, variant)
      );
    });

    if (foundVariant && !existingVariant) {
      existingVariant = foundVariant;
    }

    if (foundVariant?.designData && !existingDesign) {
      existingDesign = foundVariant.designData;
      existingVariant = foundVariant;
    }

    // Upgrade to a media-bearing design when we encounter one, even if we
    // already picked an empty design earlier in the loop.
    if (foundVariant?.designData && !designHasMedia(existingDesign) && designHasMedia(foundVariant.designData)) {
      existingDesign = foundVariant.designData;
      existingVariant = foundVariant;
    }

    if (!!existingVariant && !!existingDesign && designHasMedia(existingDesign)) {
      break;
    }
  }

  const ownDesigns = new Map<VariantInfo, DesignData>();
  variants.forEach((variant) => {
    const own = existingVariants.find(
      ({ productUid, designData }) =>
        productUid === variant.variation.productUid &&
        !!designData &&
        areAdditionalInfoEquals(
          { dimensions: designData.related_dimensions || designData.dimensions, pageCount: undefined },
          variant,
        ),
    )?.designData;
    if (own) {
      ownDesigns.set(variant, own);
    }
  });

  const designDimensions = existingDesign?.related_dimensions || existingDesign?.dimensions;
  return {
    designData: existingDesign,
    linked: existingVariant?.linked ?? true,
    ownDesigns,
    additionalInfo: existingDesign
      ? {
          dimensions: designDimensions ? { width: designDimensions.width, height: designDimensions.height } : undefined,
          pageCount: undefined,
        }
      : EMPTY_ADDTIONAL_INFO,
  };
}

export function getVariantInfoFromGroup(
  variants: VariantInfo[],
  groupKey: string,
  previousGroups: VariationGroup[] | undefined,
): SourceVariantInfo {
  const previousGroup = previousGroups?.find((group) => group.key === groupKey);

  let existingVariant: Variant | undefined;

  // eslint-disable-next-line no-restricted-syntax
  for (const variant of variants) {
    const previousVariant = previousGroup?.variationsInfo.find(
      (v) => v.variation.productUid === variant.variation.productUid && areAdditionalInfoEquals(v, variant),
    );
    if (previousVariant?.designData && !existingVariant) {
      existingVariant = previousVariant;
      break;
    }
  }

  const ownDesigns = new Map<VariantInfo, DesignData>();
  variants.forEach((variant) => {
    const own = previousGroup?.variationsInfo.find(
      (v) => v.variation.productUid === variant.variation.productUid && areAdditionalInfoEquals(v, variant),
    )?.designData;
    if (own) {
      ownDesigns.set(variant, own);
    }
  });

  return {
    designData: existingVariant?.designData,
    linked: previousGroup?.linked ?? true,
    additionalInfo: existingVariant || EMPTY_ADDTIONAL_INFO,
    ownDesigns,
  };
}

function groupVariations(
  variants: VariantInfo[],
  product: Product,
  designTemplates: { [designKey: string]: DesignData },
  getVariantSourceInfo: (variants: VariantInfo[], groupKey: string) => SourceVariantInfo,
  state: RootState,
  i18n: i18n,
  layoutPerProductUids = state.variants.layoutPerProductUids,
  groupedSpreadsPerProductUids = state.variants.groupedSpreadsPerProductUids,
  forceLayout = state.variants.configuration.forceLayout,
  keepOwnDesignsWithoutTemplate = false,
): VariationGroup[] {
  const currentDesign = state.design.designData;
  const unit = state.editor.settings.units;
  const reflectContext = getReflectContext(state, groupedSpreadsPerProductUids);

  const groupedVariations = getGroupedVariations(variants, product.groupBy);
  const sourceInfos = mapValues(groupedVariations, (groupVariants, groupKey) =>
    getVariantSourceInfo(groupVariants, groupKey),
  );

  // A variant gets its own size's artwork in the size-specific print areas: from its own design, else from its colour's
  // or a linked colour's design of the same size, else none, never the size of the design it was reflected from.
  const sizeSpecificAreas = getSizeSpecificAreas(product);
  const sizeDesignsByGroup = mapValues(groupedVariations, (groupVariants, groupKey) =>
    getDesignsBySize(
      groupVariants.map((variant) => ({ ...variant, designData: sourceInfos[groupKey].ownDesigns.get(variant) })),
      product,
    ),
  );
  const linkedGroupKeys = Object.keys(groupedVariations).filter((groupKey) => sourceInfos[groupKey].linked);
  const sizeDesignsFor = (groupKey: string, size: string) => [
    ...(sizeDesignsByGroup[groupKey].get(size) ?? []),
    ...(sourceInfos[groupKey].linked
      ? linkedGroupKeys.flatMap((linkedKey) => sizeDesignsByGroup[linkedKey].get(size) ?? [])
      : []),
  ];
  const withOwnSizeArtwork = (
    groupKey: string,
    variant: VariantInfo,
    design: DesignData,
    ownDesign: DesignData | undefined,
  ) => {
    const size = getVariationSize(variant.variation, product)?.value;
    if (!sizeSpecificAreas.length || !size) {
      return design;
    }
    return withSizeSpecificAreasFrom(
      design,
      [ownDesign, ...sizeDesignsFor(groupKey, size)],
      sizeSpecificAreas,
      reflectContext,
    );
  };

  const groups: VariationGroup[] = [];
  Object.keys(groupedVariations).forEach((groupKey) => {
    const variants = groupedVariations[groupKey];
    const firstVariant = variants[0];

    const { designData, linked, additionalInfo, ownDesigns } = sourceInfos[groupKey];

    const variationsInfo: Variant[] = variants.map((variant) => {
      let variantDesignData = designData;
      if (
        !variantDesignData ||
        variantDesignData.product_uid !== variant.variation.productUid ||
        !areAdditionalInfoEquals(variant, additionalInfo)
      ) {
        const template = designTemplates[getDesignKey(variant.variation.productUid, variant)];
        if (template) {
          // GAPI-39157: prefer a source design with the same spread count as
          // the target template so we don't leak content across spread-count
          // groups (e.g. 2-spread design onto a 1-spread template).
          let sameSpreadSource: DesignData | undefined;
          if (variantDesignData && variantDesignData.spreads.length === template.spreads.length) {
            sameSpreadSource = variantDesignData;
          } else if (currentDesign && currentDesign.spreads.length === template.spreads.length) {
            sameSpreadSource = currentDesign;
          } else {
            sameSpreadSource = variantDesignData || currentDesign;
          }
          const newDesignData = sameSpreadSource
            ? reflectDesignData(sameSpreadSource, template, reflectContext)
            : cloneDeep(template);
          variantDesignData = applyLayoutPerProductUid(
            newDesignData,
            state,
            true,
            i18n,
            layoutPerProductUids,
            groupedSpreadsPerProductUids,
            forceLayout,
          );

          const resizableElement = getResizableProductForGivenVariant(state, variantDesignData.product_uid);
          const layoutName = variantDesignData?.spreads[0].layoutSchemaName;
          const layout = layoutName ? getLayoutSchemaByName(state, layoutName) : undefined;

          if (resizableElement && layout && variantDesignData.related_dimensions) {
            variantDesignData = applyResizableProductToDesign(state, variantDesignData, resizableElement, layout, i18n);
          }
        } else {
          // reset the design to undefined until the host sends us the template; a size keeps its own saved design
          // meanwhile, as only that design holds its size-specific artwork, and so does every variant of a product
          // that is not open, as nothing requests its templates until it is
          variantDesignData =
            sizeSpecificAreas.length || keepOwnDesignsWithoutTemplate ? ownDesigns.get(variant) : undefined;
        }
      }

      return {
        ...variant,
        designData:
          variantDesignData && withOwnSizeArtwork(groupKey, variant, variantDesignData, ownDesigns.get(variant)),
      };
    });
    const group = {
      key: groupKey,
      title: getProductVariationSpecificTitle(product, firstVariant, unit),
      linked,
      outOfStock: variants.reduce<boolean>(
        (acc, variant) => acc || !!product.outOfStock[variant.variation.productUid],
        false,
      ),
      variationsInfo,
      linkingDisabled: false,
    };
    groups.push(group);
  });

  const designOptionControl = state.variants.designOptionsControl?.[0];
  const variationGroupsWithDesignOptions = state.variants.variationGroups.filter((group) => !!group.designOptions);
  // Without grouping axes a group key carries the page count, which a reopened product does not have yet when
  // its groups are first built. Its option groups are then matched by their variants, or every one of them
  // would be rebuilt linked and lose its own design.
  const findPreviousOptionGroup = (group: VariationGroup, optionKey: string, groupKey: string) => {
    const optionGroups = variationGroupsWithDesignOptions.filter(
      (groupVariation) => getDesignOptionKey(groupVariation) === optionKey,
    );
    const sameKeyGroup = optionGroups.find((groupVariation) => groupVariation.key === groupKey);
    if (sameKeyGroup || product.groupBy.length) {
      return sameKeyGroup;
    }

    const sameVariantGroup = optionGroups.find(
      (groupVariation) =>
        groupVariation.variationsInfo.length === group.variationsInfo.length &&
        group.variationsInfo.every((variant) =>
          groupVariation.variationsInfo.some((previous) => isSameVariantOfAnyPageCount(previous, variant)),
        ),
    );
    return (
      sameVariantGroup && {
        ...sameVariantGroup,
        key: groupKey,
        variationsInfo: group.variationsInfo.map((variant) => ({
          ...variant,
          designData: sameVariantGroup.variationsInfo.find((previous) => isSameVariantOfAnyPageCount(previous, variant))
            ?.designData,
        })),
      }
    );
  };

  const newGroups: VariationGroup[] = [];
  sortGroups(groups, product.productControls).forEach((group) => {
    if (designOptionControl) {
      designOptionControl.options.forEach((option) => {
        const groupKey = `${group.key}-${option.value}`;
        const existingGroup = findPreviousOptionGroup(group, option.value, groupKey);
        const sameDesignGroup = variationGroupsWithDesignOptions.find(
          (groupVariation) => getDesignOptionKey(groupVariation) === option.value && group.linked,
        );
        if (existingGroup) {
          newGroups.push(existingGroup);
        } else if (sameDesignGroup) {
          const newGroup = {
            ...group,
            designOptions: [
              {
                optionKey: option.value,
                designOptionControlSubKey: designOptionControl.subtype,
              },
            ],
            key: `${group.key}-${option.value}`,
          };
          // every option group of `group` shares its variants, so the design is copied, not written in place
          newGroup.variationsInfo = group.variationsInfo.map((variationInfo, i) => {
            const spreads = sameDesignGroup.variationsInfo[i]?.designData?.spreads;
            return variationInfo.designData && spreads
              ? { ...variationInfo, designData: { ...variationInfo.designData, spreads } }
              : variationInfo;
          });

          newGroups.push(newGroup);
        } else {
          newGroups.push({
            ...group,
            designOptions: [
              {
                optionKey: option.value,
                designOptionControlSubKey: designOptionControl.subtype,
              },
            ],
            key: `${group.key}-${option.value}`,
          });
        }
      });
    } else {
      newGroups.push(group);
    }
  });
  return newGroups;
}

export default groupVariations;
